import { Controller } from '@hotwired/stimulus'
import { throttle } from 'throttle-debounce'

type Edge = 'bottom' | 'top'

export default class StickyController extends Controller<HTMLElement> {
  declare edgeValue: Edge
  declare rootElement?: Element
  declare rootValue: string

  static values = {
    edge: { default: 'top', type: String },
    root: { default: 'body', type: String },
  }

  STUCK_CLASS = 'stuck'

  connect() {
    this.#addStickyEdgeClass()
    this.#handleScroll()
    document.addEventListener('resize', this.#handleScroll)
  }

  disconnect() {
    document.removeEventListener('resize', this.#handleScroll)
    this.rootElement?.removeEventListener('scroll', this.#handleScroll)
  }

  rootValueChanged(value: string, previousValue: string) {
    const oldRootElement = this.element.closest(previousValue)
    const newRootElement = this.element.closest(value)

    if (oldRootElement) {
      oldRootElement.removeEventListener('scroll', this.#handleScroll)
    }

    if (newRootElement) {
      this.rootElement = newRootElement
      this.rootElement.addEventListener('scroll', this.#handleScroll)
    }
  }

  // Private methods

  #addStickyEdgeClass() {
    const classes = { bottom: 'sticky-bottom', top: 'sticky-top' }

    this.element.classList.add(classes[this.edgeValue])
  }

  #edgeMeasurements(edge: Edge) {
    return {
      currentY: this.element.getBoundingClientRect()[edge],
      rootScroll: this.rootElement.scrollTop,
      rootY: this.rootElement.getBoundingClientRect()[edge],
    }
  }

  #handleBottomScroll() {
    const { currentY, rootY } = this.#edgeMeasurements('bottom')
    const isStuck = rootY - currentY === 0

    this.element.classList.toggle(this.STUCK_CLASS, isStuck)
  }

  #handleScroll = throttle(50, () => {
    this.edgeValue === 'bottom'
      ? this.#handleBottomScroll()
      : this.#handleTopScroll()
  })

  #handleTopScroll() {
    const { currentY, rootScroll, rootY } = this.#edgeMeasurements('top')
    const isScrolled = rootScroll > 0

    // HACK: We need to check for `1` in case a border is present
    const isStuck = [0, 1].includes(currentY - rootY)

    this.element.classList.toggle(this.STUCK_CLASS, isStuck && isScrolled)
  }
}
